import { useState, HTMLAttributes, useEffect, useRef, useMemo } from 'react';
import { Select, SelectProps } from '../Form';
import { Chip } from '../Chip';
import { ChevronUpIcon, ChevronDownIcon } from '../Graphics/Icons';
import { joinClassNames } from '../../utils';
import { SingleValue, MultiValue, ActionMeta, GroupBase } from 'react-select';
import { SelectComponents } from 'react-select/dist/declarations/src/components';
import { FilterChipOption, createCustomOption, DefaultRenderer } from './CustomOption';
import { getDisplayText, createFilterChipStyles } from './utils';
import { ValueContainer } from './ValueContainer';
import styles from './styles.module.scss';

export type FilterChipProps<
  T = string,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<FilterChipOption<T>> = GroupBase<FilterChipOption<T>>,
> = Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  label: string;
  value?: T | T[];
  options: FilterChipOption<T>[];
  onChange: (value: SingleValue<FilterChipOption<T>> | MultiValue<FilterChipOption<T>>) => void;
  className?: string;
  /**
   * Whether to allow multiple selections
   * @default false
   */
  isMulti?: IsMulti;
  /**
   * Hide the input component in single select mode only
   * @default true
   */
  hideInput?: boolean;
  /**
   * Override props for the Select component
   */
  selectProps?: Omit<
    SelectProps<FilterChipOption<T>, IsMulti, Group>,
    'options' | 'value' | 'onChange' | 'id' | 'label' | 'isMulti'
  >;
  /**
   * Override props for the Chip component
   */
  chipProps?: Omit<React.ComponentProps<typeof Chip>, 'children' | 'active' | 'onClick'>;
};

export const FilterChip = <
  T = string,
  IsMulti extends boolean = false,
  Group extends GroupBase<FilterChipOption<T>> = GroupBase<FilterChipOption<T>>,
>({
  label,
  value,
  options,
  onChange,
  className,
  selectProps,
  chipProps,
  isMulti,
  hideInput = true,
  ...props
}: FilterChipProps<T, IsMulti, Group>) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedOptions = Array.isArray(value)
    ? options.filter((opt) => value.includes(opt.value))
    : options.filter((opt) => opt.value === value);

  const shouldHideInput = isMulti !== true && hideInput;

  const filterChipStyles = useMemo(
    () => createFilterChipStyles<T, IsMulti, Group>(shouldHideInput),
    [shouldHideInput]
  );

  const selectComponents = useMemo(
    (): Partial<SelectComponents<FilterChipOption<T>, IsMulti, Group>> => ({
      DropdownIndicator: null,
      IndicatorSeparator: null,
      ValueContainer: ValueContainer<T, IsMulti, Group>,
      Option: createCustomOption<T, IsMulti, Group>(DefaultRenderer<T>),
    }),
    []
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleChange = (
    newValue: SingleValue<FilterChipOption<T>> | MultiValue<FilterChipOption<T>>,
    _: ActionMeta<FilterChipOption<T>>
  ) => {
    onChange(newValue);
    if (selectProps?.closeMenuOnSelect === true) {
      setIsOpen(false);
    }
  };

  const displayText = getDisplayText<T>(selectedOptions, isMulti ?? false);

  return (
    <div ref={menuRef} className={joinClassNames(styles.filterChip, className)} {...props}>
      <Chip
        active={!!selectedOptions.length}
        onClick={() => setIsOpen((prev) => !prev)}
        className={styles.chipContent}
        {...chipProps}
      >
        <span>
          {label}
          {displayText != '' && <span className={styles.displayText}>{displayText}</span>}
        </span>
        {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Chip>
      {isOpen && (
        <div className={styles.menu}>
          <Select<FilterChipOption<T>, IsMulti, Group>
            id={`filter-chip-select-${label.toLowerCase().replace(/\s+/g, '-')}`}
            label={label}
            hideLabel
            isMulti={isMulti}
            options={options}
            value={selectedOptions}
            onChange={handleChange}
            autoFocus={selectProps?.autoFocus ?? true}
            menuIsOpen
            closeMenuOnSelect={selectProps?.closeMenuOnSelect ?? false}
            hideSelectedOptions={selectProps?.hideSelectedOptions ?? false}
            components={{ ...selectComponents, ...selectProps?.components }}
            styles={{ ...filterChipStyles, ...selectProps?.styles }}
            {...selectProps}
          />
        </div>
      )}
    </div>
  );
};
